import { useScrollReveal } from '@/hooks/useScrollReveal';

const differentiators = [
  {
    icon: 'ri-shield-check-line',
    title: 'Ethics-Led, Always',
    description: 'No shortcuts, no black-box processes. Every search runs on transparency, integrity, and a commitment to doing right by both clients and candidates.',
    accent: 'accent',
  },
  {
    icon: 'ri-user-star-line',
    title: 'Director-Led Engagements',
    description: 'Your account isn\'t handed off to junior recruiters. Our directors personally lead every engagement — you get senior thinking from day one.',
    accent: 'primary',
  },
  {
    icon: 'ri-building-line',
    title: 'Deep Industry Knowledge',
    description: 'We don\'t just recruit — we understand the sectors we serve. From automotive to fintech, we speak your industry\'s language and know where the talent hides.',
    accent: 'secondary',
  },
  {
    icon: 'ri-flashlight-line',
    title: 'Speed Without Sacrifice',
    description: 'We move fast — but never at the expense of quality. Our structured search methodology delivers the right candidates in record time.',
    accent: 'accent',
  },
  {
    icon: 'ri-hand-heart-line',
    title: 'Partnerships, Not Transactions',
    description: 'We embed ourselves in your culture and goals. Most clients stay with us for years — because we earn that trust with every placement.',
    accent: 'primary',
  },
  {
    icon: 'ri-trophy-line',
    title: 'Proven Since 2017',
    description: '200+ client partners, thousands of successful placements, and a growing reputation built entirely on word-of-mouth referrals.',
    accent: 'secondary',
  },
];

const accentStyles: Record<string, { iconBg: string; iconColor: string; borderHover: string; dot: string }> = {
  primary: {
    iconBg: 'bg-primary-100',
    iconColor: 'text-primary-500',
    borderHover: 'hover:border-primary-300',
    dot: 'bg-primary-400',
  },
  accent: {
    iconBg: 'bg-accent-100',
    iconColor: 'text-accent-500',
    borderHover: 'hover:border-accent-300',
    dot: 'bg-accent-400',
  },
  secondary: {
    iconBg: 'bg-secondary-100',
    iconColor: 'text-secondary-500',
    borderHover: 'hover:border-secondary-300',
    dot: 'bg-secondary-400',
  },
};

export default function WhyChooseUsSection() {
  const { ref: headerRef, isVisible: headerVisible } = useScrollReveal(0.1);
  const { ref: gridRef, isVisible: gridVisible } = useScrollReveal(0.05);

  return (
    <section id="why-choose-us" className="relative bg-background-100 py-24 md:py-32 px-6 md:px-10 lg:px-16">
      <div className="max-w-7xl mx-auto">
        <div
          ref={headerRef}
          className={`text-center mb-16 md:mb-20 transition-all duration-700 ${
            headerVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
          }`}
        >
          <span className="inline-block px-4 py-1.5 rounded-full bg-accent-100 text-accent-600 text-xs font-bold tracking-widest uppercase mb-5">
            Why Choose Us
          </span>
          <h2 className="font-heading text-3xl md:text-5xl lg:text-6xl font-bold text-foreground-900 leading-[1.1] max-w-3xl mx-auto">
            Not Just Another{' '}
            <span className="italic" style={{ color: '#f0a0b5' }}>Recruitment Firm</span>
          </h2>
          <p className="mt-5 text-foreground-500 text-base md:text-lg max-w-xl mx-auto leading-relaxed">
            Here&apos;s what separates us from every other recruitment consultant you&apos;ve worked with.
          </p>
        </div>

        <div
          ref={gridRef}
          className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5 md:gap-6"
        >
          {differentiators.map((item, idx) => {
            const styles = accentStyles[item.accent];
            return (
              <div
                key={item.title}
                className={`group relative rounded-2xl bg-background-50 border border-background-200 ${styles.borderHover} p-6 md:p-7 transition-all duration-400 ${
                  gridVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-6'
                }`}
                style={{ transitionDelay: gridVisible ? `${idx * 80}ms` : '0ms' }}
              >
                {/* Quality dot */}
                <div className={`absolute top-4 right-4 w-2.5 h-2.5 rounded-full ${styles.dot} opacity-40 group-hover:opacity-100 transition-opacity duration-300`}></div>

                <div className={`w-12 h-12 flex items-center justify-center rounded-xl ${styles.iconBg} mb-5 group-hover:scale-110 transition-transform duration-300`}>
                  <i className={`${item.icon} ${styles.iconColor} text-xl`}></i>
                </div>
                <h3 className="font-heading text-xl md:text-2xl font-bold text-foreground-900 mb-2">
                  {item.title}
                </h3>
                <p className="text-foreground-500 text-sm leading-relaxed">
                  {item.description}
                </p>
              </div>
            );
          })}
        </div>
      </div>
    </section>
  );
}